import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { articlesApi } from '@shared/api';
import { Alert, AlertTitle, Heading, Label, RadioGroup, RadioGroupItem, Text } from '@shared/ui';
import { articleKeys } from '../api/queries';
import { ArticleError } from './ArticleError';

export function ArticleReplacementReview({
  scope,
  candidateId,
  approving,
  decision,
  required,
  disabled,
  onDecision,
}: {
  scope: string;
  candidateId: string;
  approving: boolean;
  decision?: 'replace' | 'keep_existing';
  required: boolean;
  disabled: boolean;
  onDecision: (decision: 'replace' | 'keep_existing') => void;
}) {
  const { t } = useTranslation();
  const candidate = useQuery({
    queryKey: articleKeys.detail(scope, candidateId),
    queryFn: ({ signal }) => articlesApi.detail(candidateId, signal),
    retry: false,
  });
  return (
    <section className="min-w-0 space-y-3" aria-label={t('articles.review.existing')}>
      <Heading as="h3" size="sm">
        {t('articles.review.existing')}
      </Heading>
      {candidate.isPending ? (
        <Text role="status">{t('common.loading')}</Text>
      ) : candidate.error ? (
        <ArticleError
          error={candidate.error}
          onRetry={() => {
            void candidate.refetch();
          }}
        />
      ) : (
        <details className="min-w-0 rounded-md border border-border p-3">
          <summary className="cursor-pointer break-words">{candidate.data.data.title}</summary>
          <Text className="max-h-64 overflow-auto whitespace-pre-wrap break-words pt-3">
            {candidate.data.data.content}
          </Text>
        </details>
      )}
      <Text size="sm">{t('articles.review.explanation')}</Text>
      {approving ? (
        <>
          <RadioGroup
            value={decision ?? ''}
            disabled={disabled || !candidate.data || Boolean(candidate.error)}
            aria-label={t('articles.review.decision')}
            aria-required="true"
            onValueChange={(value) => {
              if (value === 'replace' || value === 'keep_existing') onDecision(value);
            }}
          >
            {(['replace', 'keep_existing'] as const).map((value) => (
              <div key={value} className="flex items-start gap-2">
                <RadioGroupItem value={value} id={`article-replacement-${value}`} />
                <Label htmlFor={`article-replacement-${value}`}>
                  {t(`articles.review.${value}`)}
                </Label>
              </div>
            ))}
          </RadioGroup>
          {required ? (
            <Alert variant="destructive">
              <AlertTitle>{t('articles.review.required')}</AlertTitle>
            </Alert>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
